import { API_ROUTES } from "@/api/endpoints";

export const INSTITUTION_ADMIN_ENDPOINTS = {
  DASHBOARD: {
    STATS: (institutionId: number) => `${API_ROUTES.INSTITUTION_DASHBOARD_STATS}/${institutionId}`,
  },

  GLOBAL_SEARCH: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/search`,

  INSTITUTION: {
    DETAIL: (id: number) => `${API_ROUTES.INSTITUTIONS}/${id}`,
    UPDATE: (id: number) => `${API_ROUTES.INSTITUTIONS}/${id}`,
    SETTINGS: (id: number) => `${API_ROUTES.INSTITUTIONS}/${id}/settings`,
    ADMISSION_READINESS: (id: number) => `${API_ROUTES.INSTITUTIONS}/${id}/admission-readiness`,
    PUSH_STATUS: (id: number) => `${API_ROUTES.INSTITUTIONS}/${id}/push-status`,
    MAP_CONFIG: (id: number) => `${API_ROUTES.INSTITUTIONS}/${id}/map-config`,
  },

  ALUMNI: {
    BASE: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/alumni`,
  },

  WHATSAPP: {
    WALLET: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/whatsapp/wallet`,
    LOGIN_WIDGET_URL: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/whatsapp/login-widget-url`,
    TRIGGERS: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/whatsapp/triggers`,
    CONNECTION_STATUS: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/whatsapp/connection-status`,
    TOKENS: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/whatsapp/tokens`,
  },

  SUBSCRIPTIONS: {
    GET_FOR_INSTITUTION: (institutionId: number) => `${API_ROUTES.SUBSCRIPTIONS}/institution/${institutionId}`,
    UPGRADE_REQUESTS: (subscriptionId: number) => `${API_ROUTES.SUBSCRIPTIONS}/${subscriptionId}/upgrade-requests`,
  },

  ACADEMIC_STRUCTURE: {
    TREE: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/academic-structure`,
    GROUPS: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/academic-groups`,
    GROUP_SUBJECTS: (institutionId: number) =>
      `${API_ROUTES.INSTITUTIONS}/${institutionId}/academic-groups/subjects`,
  },

  ROLES: {
    LIST: (institutionId: number) => `${API_ROUTES.ROLES_INSTITUTION}/${institutionId}`,
    CREATE: (institutionId: number) => `${API_ROUTES.ROLES_INSTITUTION}/${institutionId}`,
    UPDATE: (institutionId: number, roleId: number) => `${API_ROUTES.ROLES_INSTITUTION}/${institutionId}/${roleId}`,
    DELETE: (institutionId: number, roleId: number) => `${API_ROUTES.ROLES_INSTITUTION}/${institutionId}/${roleId}`,
  },

  TEACHERS: {
    OVERVIEW: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/teachers/overview`,
  },

  STAFF: {
    LIST: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/staff`,
    BY_ROLE: (institutionId: number, roleName: string) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/staff/by-role/${roleName}`,
    CREATE: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/staff`,
    DETAIL: (institutionId: number, staffId: number) =>
      `${API_ROUTES.INSTITUTIONS}/${institutionId}/staff/${staffId}`,
    UPDATE: (institutionId: number, staffId: number) =>
      `${API_ROUTES.INSTITUTIONS}/${institutionId}/staff/${staffId}`,
    ATTENDANCE: (institutionId: number, staffId: number) =>
      `${API_ROUTES.INSTITUTIONS}/${institutionId}/staff/${staffId}/attendance`,
    ATTENDANCE_ROSTER: (institutionId: number) =>
      `${API_ROUTES.INSTITUTIONS}/${institutionId}/staff-attendance`,
    CHANGE_ROLE: (institutionId: number, staffId: number) =>
      `${API_ROUTES.INSTITUTIONS}/${institutionId}/staff/${staffId}/role`,
    RESET_PASSWORD: (userId: number) => `${API_ROUTES.USERS}/${userId}/reset-password`,
    CHANGE_EMAIL: (userId: number) => `${API_ROUTES.USERS}/${userId}/change-email`,
    GRANT_LOGIN: (institutionId: number, staffId: number) =>
      `${API_ROUTES.INSTITUTIONS}/${institutionId}/staff/${staffId}/grant-login`,
    SALARY: {
      COMPONENTS: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/staff-salary-components`,
      STRUCTURE: (institutionId: number, staffId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/staff/${staffId}/salary-structure`,
      STRUCTURE_HISTORY: (institutionId: number, staffId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/staff/${staffId}/salary-structure-history`,
      MONTHLY_PAYABLE: (institutionId: number, staffId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/staff/${staffId}/salary-structure/monthly-payable`,
    },
    ASSIGNMENTS: {
      LIST: (institutionId: number, staffId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/staff/${staffId}/assignments`,
      CREATE: (institutionId: number, staffId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/staff/${staffId}/assignments`,
      REMOVE: (institutionId: number, staffId: number, assignmentId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/staff/${staffId}/assignments/${assignmentId}`,
      HISTORY: (institutionId: number, staffId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/staff/${staffId}/assignments-history`,
    },
  },

  UPLOADS: {
    LOGO: API_ROUTES.UPLOADS_LOGO,
    STAFF_PHOTO: API_ROUTES.UPLOADS_STAFF_PHOTO,
    BOOK_COVER: API_ROUTES.UPLOADS_BOOK_COVER,
    STUDENT_PHOTO: API_ROUTES.UPLOADS_STUDENT_PHOTO, 
  },

  // List/admit are institution-scoped; detail/enrollments/promote/withdraw
  // hang off the student id directly (see routes/index.js — studentRoutes
  // vs studentDetailRoutes). Admission itself (creating a new student) is
  // handled by the Admissions module's Direct/Transfer/convert flows, not
  // here — this block is for browsing, viewing, promoting, and withdrawing
  // students already on roll.
  STUDENTS: {
    LIST: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/students`,
    DETAIL: (studentId: number) => `${API_ROUTES.STUDENTS}/${studentId}`,
    ENROLLMENTS: (studentId: number) => `${API_ROUTES.STUDENTS}/${studentId}/enrollments`,
    PROMOTE: (studentId: number) => `${API_ROUTES.STUDENTS}/${studentId}/promote`,
    CHANGE_SECTION: (studentId: number) => `${API_ROUTES.STUDENTS}/${studentId}/change-section`,
    JOIN_DATE: (studentId: number) => `${API_ROUTES.STUDENTS}/${studentId}/join-date`,
    ROLL_NUMBER: (studentId: number) => `${API_ROUTES.STUDENTS}/${studentId}/roll-number`,
    WITHDRAW: (studentId: number) => `${API_ROUTES.STUDENTS}/${studentId}/withdraw`,
    // POST /users/:userId/reset-password — userId is the student's login account (StudentDetail.userId)
    RESET_PASSWORD: (userId: number) => `${API_ROUTES.USERS}/${userId}/reset-password`,
    CHANGE_EMAIL: (userId: number) => `${API_ROUTES.USERS}/${userId}/change-email`,
    DOCUMENTS: {
      LIST: (studentId: number) => `${API_ROUTES.STUDENTS}/${studentId}/documents`,
      UPLOAD: (studentId: number, documentType: string) => `${API_ROUTES.STUDENTS}/${studentId}/documents/${documentType}`,
      DELETE: (studentId: number, documentType: string) => `${API_ROUTES.STUDENTS}/${studentId}/documents/${documentType}`,
    },
  },

  FEES: {
    COMPONENTS: {
      LIST: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/fees/components`,
      CREATE: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/fees/components`,
      UPDATE: (institutionId: number, componentId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/fees/components/${componentId}`,
      DELETE: (institutionId: number, componentId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/fees/components/${componentId}`,
    },
    /** Fee collection worklist (paged) and the counts behind its status chips. */
    COLLECTION: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/fees/collection`,
    COLLECTION_COUNTS: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/fees/collection/counts`,
    FORMER_STUDENT_DUES: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/fees/former-students-dues`,
    STRUCTURES: {
      LIST: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/fees/structures`,
      GET: (institutionId: number, structureId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/fees/structures/${structureId}`,
      CREATE: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/fees/structures`,
      UPDATE: (institutionId: number, structureId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/fees/structures/${structureId}`,
      DELETE: (institutionId: number, structureId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/fees/structures/${structureId}`,
    },
    INVOICES: {
      GENERATE: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/fees/invoices/generate`,
      REGENERATE: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/fees/invoices/regenerate`,
      GENERATE_BULK: (institutionId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/fees/invoices/generate-bulk`,
      BY_STUDENT: (institutionId: number, studentId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/fees/students/${studentId}/invoices`,
      GET: (institutionId: number, invoiceId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/fees/invoices/${invoiceId}`,
    },
    PAYMENTS: {
      LIST: (institutionId: number, invoiceId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/fees/invoices/${invoiceId}/payments`,
      CREATE: (institutionId: number, invoiceId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/fees/invoices/${invoiceId}/payments`,
    },
    DISCOUNTS: {
      LIST: (institutionId: number, invoiceId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/fees/invoices/${invoiceId}/discounts`,
      CREATE: (institutionId: number, invoiceId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/fees/invoices/${invoiceId}/discounts`,
      REVOKE: (institutionId: number, invoiceId: number, discountId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/fees/invoices/${invoiceId}/discounts/${discountId}`,
    },
    ADHOC_CHARGES: {
      LIST: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/fees/adhoc-charges`,
      CREATE: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/fees/adhoc-charges`,
      CANCEL: (institutionId: number, chargeId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/fees/adhoc-charges/${chargeId}/cancel`,
    },
    REPORTS: {
      OVERVIEW: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/fees/reports/overview`,
      BREAKDOWN: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/fees/reports/breakdown`,
    },
    BANK_ACCOUNTS: {
      LIST: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/fees/bank-accounts`,
      CREATE: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/fees/bank-accounts`,
      UPDATE: (institutionId: number, accountId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/fees/bank-accounts/${accountId}`,
      DELETE: (institutionId: number, accountId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/fees/bank-accounts/${accountId}`,
    },
  },

  CASH_BOOK: {
    BASE: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/cash-book`,
  },

  FINANCE: {
    EXPENSE_CATEGORIES: {
      LIST: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/finance/expense-categories`,
      CREATE: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/finance/expense-categories`,
      UPDATE: (institutionId: number, categoryId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/finance/expense-categories/${categoryId}`,
      DELETE: (institutionId: number, categoryId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/finance/expense-categories/${categoryId}`,
    },
    EXPENSES: {
      LIST: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/finance/expenses`,
      CREATE: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/finance/expenses`,
      GET: (institutionId: number, expenseId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/finance/expenses/${expenseId}`,
      UPDATE: (institutionId: number, expenseId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/finance/expenses/${expenseId}`,
      CANCEL: (institutionId: number, expenseId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/finance/expenses/${expenseId}/cancel`,
    },
    TRANSACTIONS: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/finance/transactions`,
    REPORTS: {
      OVERVIEW: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/finance/reports/overview`,
      EXPENSE_BREAKDOWN: (institutionId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/finance/reports/expense-breakdown`,
      ACCOUNT_BREAKDOWN: (institutionId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/finance/reports/account-breakdown`,
    },
  },

  PROMOTIONS: {
    PREVIEW: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/promotions/preview`,
    BATCHES: {
      LIST: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/promotions/batches`,
      CREATE: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/promotions/batches`,
      GET: (institutionId: number, batchId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/promotions/batches/${batchId}`,
      CONFIRM: (institutionId: number, batchId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/promotions/batches/${batchId}/confirm`,
      REVERT: (institutionId: number, batchId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/promotions/batches/${batchId}/revert`,
    },
    ITEMS: {
      UPDATE: (institutionId: number, batchId: number, itemId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/promotions/batches/${batchId}/items/${itemId}`,
    },
  },

  ADMISSIONS: {
    INQUIRIES: {
      LIST: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/admissions/inquiries`,
      CREATE: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/admissions/inquiries`,
      GET: (institutionId: number, inquiryId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/admissions/inquiries/${inquiryId}`,
      UPDATE: (institutionId: number, inquiryId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/admissions/inquiries/${inquiryId}`,
      STATUS: (institutionId: number, inquiryId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/admissions/inquiries/${inquiryId}/status`,
      CONVERT: (institutionId: number, inquiryId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/admissions/inquiries/${inquiryId}/convert`,
    },
    DIRECT: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/admissions/direct`,
    TRANSFER: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/admissions/transfer`,
    READMIT: (institutionId: number, studentId: number) =>
      `${API_ROUTES.INSTITUTIONS}/${institutionId}/admissions/students/${studentId}/readmit`,
    LOGIN_AVAILABILITY: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/admissions/login-availability`,
    REGISTRATION_PREVIEW: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/admissions/registration-preview`,
    ADMISSION_FEE: (institutionId: number, studentId: number) =>
      `${API_ROUTES.INSTITUTIONS}/${institutionId}/admissions/students/${studentId}/admission-fee`,
    RELEASE_CREDENTIALS: (institutionId: number, studentId: number) =>
      `${API_ROUTES.INSTITUTIONS}/${institutionId}/admissions/students/${studentId}/release-credentials`,
    APPLICATIONS: {
      LIST: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/admissions/applications`,
      GET: (institutionId: number, applicationId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/admissions/applications/${applicationId}`,
      APPROVE: (institutionId: number, applicationId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/admissions/applications/${applicationId}/approve`,
      REJECT: (institutionId: number, applicationId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/admissions/applications/${applicationId}/reject`,
      REGISTRATION_FEE: (institutionId: number, applicationId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/admissions/applications/${applicationId}/registration-fee`,
    },
    QR: {
      INFO: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/admissions/qr-code/info`,
      DOWNLOAD: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/admissions/qr-code/download`,
      PREVIEW: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/admissions/qr-code/preview`,
      REGENERATE: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/admissions/qr-code/regenerate`,
    },
    FORM: {
      GET: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/admissions/form`,
      SET_OVERRIDE: (institutionId: number, fieldKey: string) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/admissions/form/${fieldKey}`,
      CLEAR_OVERRIDE: (institutionId: number, fieldKey: string) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/admissions/form/${fieldKey}`,
    },
  },

  ATTENDANCE: {
    ROSTER: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/attendance/roster`,
    MARK: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/attendance/mark`,
    SUMMARY: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/attendance/summary`,
    ELIGIBILITY: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/attendance/eligibility`,
    STUDENT_LOG: (institutionId: number, studentId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/attendance/log/student/${studentId}`,
    GRACE: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/attendance/grace`,
    GRACE_REVIEW: (institutionId: number, graceId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/attendance/grace/${graceId}/review`,
    EXAM_ELIGIBILITY: (institutionId: number, examId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/attendance/exams/${examId}/eligibility`,
    EXAM_OVERRIDE: (institutionId: number, examId: number, studentId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/attendance/exams/${examId}/eligibility/override/${studentId}`,
    EXAM_FREEZE: (institutionId: number, examId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/attendance/exams/${examId}/eligibility/freeze`,
    SUBJECTS: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/attendance/subjects`,
    SUBJECT_ROSTER: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/attendance/subject-roster`,
    SUBJECT_MARK: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/attendance/subject-mark`,
    SUBJECT_RULES: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/attendance/subject-rules`,
    OVERVIEW: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/attendance/overview`,
    INTEGRITY: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/attendance/integrity`,
    INTEGRITY_REBUILD: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/attendance/integrity/rebuild`,
    CLASS_LOG: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/attendance/log`,
    SESSION: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/attendance/session`,
    POLICY: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/attendance/policy`,
    CALENDAR: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/attendance/calendar`,
    CALENDAR_DAY: (institutionId: number, date: string) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/attendance/calendar/${date}`,
    REQUIRED_DAYS: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/attendance/required-days`,
  },

  STAFF_ATTENDANCE: {
    ROSTER: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/staff-attendance/roster`,
    MARK: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/staff-attendance/mark`,
    SUMMARY: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/staff-attendance/summary`,
  },

  LEAVE: {
    BASE: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/leave`,
  },

  STAFF_DAILY_REPORTS: {
    LIST: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/staff-daily-reports`,
    REVIEW: (institutionId: number, reportId: number) =>
      `${API_ROUTES.INSTITUTIONS}/${institutionId}/staff-daily-reports/${reportId}/review`,
  },

  SYLLABUS: {
    MINE: (institutionId: number, staffAssignmentId: number) =>
      `${API_ROUTES.INSTITUTIONS}/${institutionId}/syllabus/assignments/${staffAssignmentId}`,
    MASTER: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/syllabus/master`,
    SUBJECT_TEACHERS: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/syllabus/subject-teachers`,
    PUBLISH: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/syllabus/master/publish`,
    UNPUBLISH: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/syllabus/master/unpublish`,
    PRINT_SETTINGS: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/syllabus/print-settings`,
  },

  FACILITIES: {
    LIBRARY: {
      BOOKS: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/library/books`,
      BOOK: (institutionId: number, bookId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/library/books/${bookId}`,
      ISSUE: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/library/issues`,
      RETURN: (institutionId: number, issueId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/library/issues/${issueId}/return`,
      STUDENT_ISSUES: (institutionId: number, studentId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/library/students/${studentId}/issues`,
      MEMBERS: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/library/members`,
      MEMBER: (institutionId: number, studentId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/library/members/${studentId}`,
      PEOPLE: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/library/people`,
      SCAN: (institutionId: number, token: string) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/library/scan/${encodeURIComponent(token)}`,
      STAFF_ISSUES: (institutionId: number, staffId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/library/staff/${staffId}/issues`,
      RENEW: (institutionId: number, issueId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/library/issues/${issueId}/renew`,
      OVERDUE: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/library/overdue`,
      SETTINGS: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/library/settings`,
      COPIES: (institutionId: number, bookId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/library/books/${bookId}/copies`,
      COPY: (institutionId: number, bookId: number, copyId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/library/books/${bookId}/copies/${copyId}`,
    },
    TRANSPORT: {
      ROUTES: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/transport/routes`,
      ROUTE: (institutionId: number, routeId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/transport/routes/${routeId}`,
      ASSIGN: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/transport/assignments`,
      VOID_BOARDING: (institutionId: number, boardingId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/transport/boardings/${boardingId}/void`,
      CARRY_FORWARD: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/transport/assignments/carry-forward`,
      END: (institutionId: number, assignmentId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/transport/assignments/${assignmentId}/end`,
      STUDENT_ASSIGNMENT: (institutionId: number, studentId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/transport/students/${studentId}/assignment`,
      DRIVER_ROUTES: (institutionId: number, staffId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/transport/drivers/${staffId}/routes`,
      TRIPS: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/transport/trips`,
      LIVE: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/transport/live`,
      STOPS: (institutionId: number, routeId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/transport/routes/${routeId}/stops`,
      ASSIGNMENT_STOP: (institutionId: number, assignmentId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/transport/assignments/${assignmentId}/stop`,
      BOARDINGS: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/transport/boardings`,
    },
    FEE_PLANS: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/facility-fee-plans`,
    HOSTEL: {
      BUILDINGS: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/hostel/buildings`,
      ROOMS: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/hostel/rooms`,
      ALLOCATE: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/hostel/allocations`,
      VACATE: (institutionId: number, allocationId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/hostel/allocations/${allocationId}/vacate`,
      STUDENT_ALLOCATION: (institutionId: number, studentId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/hostel/students/${studentId}/allocation`,
      BUILDING: (institutionId: number, buildingId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/hostel/buildings/${buildingId}`,
      ROOM: (institutionId: number, roomId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/hostel/rooms/${roomId}`,
      ROOM_BEDS: (institutionId: number, roomId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/hostel/rooms/${roomId}/beds`,
      TRANSFER: (institutionId: number, allocationId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/hostel/allocations/${allocationId}/transfer`,
      BASE: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/hostel`,
      MESS: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/hostel/mess`,
      ALLOCATION_FEE: (institutionId: number, allocationId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/hostel/allocations/${allocationId}/fee`,
      STAFF: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/hostel/staff`,
      STAFF_END: (institutionId: number, assignmentId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/hostel/staff/${assignmentId}/end`,
      SETTINGS: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/hostel/settings`,
    },
    INVENTORY: {
      ITEMS: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/inventory/items`,
      ITEM: (institutionId: number, itemId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/inventory/items/${itemId}`,
      TRANSACTIONS: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/inventory/transactions`,
      ITEM_TRANSACTIONS: (institutionId: number, itemId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/inventory/items/${itemId}/transactions`,
      ISSUE: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/inventory/issues`,
      STUDENT_ISSUES: (institutionId: number, studentId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/inventory/students/${studentId}/issues`,
      ISSUE_RETURNS: (institutionId: number, issueId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/inventory/issues/${issueId}/returns`,
      HISTORY: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/inventory/history`,
      SUMMARY: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/inventory/reports/summary`,
      PENALTIES: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/inventory/penalties`,
      PENALTY_ACTION: (institutionId: number, penaltyId: number, action: "charge" | "waive" | "reverse") =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/inventory/penalties/${penaltyId}/${action}`,
      ITEM_LOCATIONS: (institutionId: number, itemId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/inventory/items/${itemId}/locations`,
      ITEM_CONDITION: (institutionId: number, itemId: number, action: "damage" | "repair") =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/inventory/items/${itemId}/${action}`,
      ITEM_STOCKTAKE: (institutionId: number, itemId: number) =>
        `${API_ROUTES.INSTITUTIONS}/${institutionId}/facilities/inventory/items/${itemId}/stocktake`,
    },
  },
  PAYROLL_V2: {
    BASE: (institutionId: number) => `${API_ROUTES.INSTITUTIONS}/${institutionId}/payroll`,
    STAFF_REVISIONS: (institutionId: number, staffId: number) =>
      `${API_ROUTES.INSTITUTIONS}/${institutionId}/staff/${staffId}/pay-revisions`,
  },
};